import { useSyncExternalStore } from 'react';

/**
 * Feature flags for the 2609 pivot.
 *
 * Resolution order, highest first:
 *   1. A Directus `app_flags` row (staff can flip a flag without a deploy)
 *   2. A build-time REACT_APP_FLAG_<NAME> env var
 *   3. false
 *
 * A flag is a switch, never a secret. Directus Community cannot hide rows, so every
 * `app_flags` value is publicly readable — never gate anything security-sensitive on one.
 */

export const FLAGS = [
  'login_wall',      // the login-first front door itself, so it can be switched off
  'home_switch',     // Services / Tracker / Updates segmented control on Home
  'tracker',
  'tracker_tar',     // the T, A and R starter packs
  'tracker_haid',    // haid & nifas mode — sensitive, needs asatizah + DPO sign-off
  'tracker_compare',
  'tracker_community',
  'updates_view',
  'chat_live',       // transport undecided; off keeps today's seeded behaviour
  'install_prompt',
];

// CRA's DefinePlugin only substitutes statically written `process.env.REACT_APP_*`
// expressions. `process.env[name]` is undefined in the browser bundle, so every flag
// has to be spelled out here.
const ENV_FLAGS = {
  login_wall: process.env.REACT_APP_FLAG_LOGIN_WALL,
  home_switch: process.env.REACT_APP_FLAG_HOME_SWITCH,
  tracker: process.env.REACT_APP_FLAG_TRACKER,
  tracker_tar: process.env.REACT_APP_FLAG_TRACKER_TAR,
  tracker_haid: process.env.REACT_APP_FLAG_TRACKER_HAID,
  tracker_compare: process.env.REACT_APP_FLAG_TRACKER_COMPARE,
  tracker_community: process.env.REACT_APP_FLAG_TRACKER_COMMUNITY,
  updates_view: process.env.REACT_APP_FLAG_UPDATES_VIEW,
  chat_live: process.env.REACT_APP_FLAG_CHAT_LIVE,
  install_prompt: process.env.REACT_APP_FLAG_INSTALL_PROMPT,
};

const truthy = (value) => value === true || value === 'true' || value === 1 || value === '1';

// Directus rows, once they arrive. Empty until then, so flags read as env-or-false
// on first paint rather than flickering on.
let remote = {};
const listeners = new Set();

/**
 * Called by OfferingsProvider with whatever `app_flags` returned. A missing or
 * forbidden collection yields [], which simply leaves every flag to the env layer.
 */
export function setRemoteFlags(rows) {
  const next = {};
  for (const row of rows || []) {
    const slug = row && (row.slug || row.key || row.name);
    if (slug) next[slug] = truthy(row.enabled);
  }
  const changed = FLAGS.some((name) => next[name] !== remote[name]);
  remote = next;
  if (changed) listeners.forEach((listener) => listener());
}

export function getFlag(name) {
  if (Object.prototype.hasOwnProperty.call(remote, name)) return remote[name];
  return truthy(ENV_FLAGS[name]);
}

function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useFlag(name) {
  return useSyncExternalStore(
    subscribe,
    () => getFlag(name),
    () => false,
  );
}
